import { format } from 'date-fns';
import { IStringifyOptions, parse, stringify } from 'qs';
import { QueryParams, RequestStatus } from 'types/transport';

// API request parameter date format
export const queryDateFormat = 'yyyy-MM-dd';

/**
 * Decode a URL parameter string into an object
 *
 * @param {string} query Source query string
 */
export const decodeQuery = (query?: string): QueryParams =>
  parse(query ?? '', {
    ignoreQueryPrefix: true,
  }) as QueryParams;

/**
 * Encode a query object into a query string
 *
 * @param {QueryParams} query Query parameter object
 * @param {string} arrayFormat Array format
 */
export const encodeQuery = (
  query?: Maybe<QueryParams>,
  arrayFormat?: IStringifyOptions['arrayFormat'],
) =>
  query
    ? stringify(query, {
        arrayFormat,
        charset: 'utf-8',
        serializeDate: date => format(date, 'yyyy-MM-dd'),
        skipNulls: true,
      })
    : undefined;

/**
 * Check if all values match the specified status
 *
 * @param {RequestStatus} expectation Target request status to validate against
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
const isStatus = (expectation: RequestStatus, statuses: Array<RequestStatus>) =>
  statuses.every(status => status === expectation);

/**
 * Check if the specified status denotes an awaiting request
 *
 * @param {RequestStatus} status Request status to check
 */
export const isAwaitingStatus = (status: RequestStatus) =>
  status === RequestStatus.None || isPendingStatus(status);

/**
 * Check if the specified status denotes a pending request
 *
 * @param {RequestStatus} status Request status to check
 */
export const isPendingStatus = (status: RequestStatus) =>
  [RequestStatus.Creating, RequestStatus.Reading, RequestStatus.Updating].includes(status);

/**
 * Check if all specified statuses denote a creating status
 *
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
export const isCreatingStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.Creating, statuses);

/**
 * Check if all specified statuses denote an error status
 *
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
export const isErrorStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.Error, statuses);

/**
 * Check if all specified statuses denote no status
 *
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
export const isNoneStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.None, statuses);

/**
 * Check if all specified statuses denote a reading status
 *
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
export const isReadingStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.Reading, statuses);

/**
 * Check if all specified statuses denote a success status
 *
 * @param {Array<RequestStatus>} statuses List of statuses to verify
 */
export const isSuccessStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.Success, statuses);

/**
 * Check if all specified statuses denote a creating status
 *
 * @param {Array<RequestStatus>} statuses List of updating to verify
 */
export const isUpdatingStatus = (...statuses: Array<RequestStatus>) =>
  isStatus(RequestStatus.Updating, statuses);
